import { Inject, Injectable, Injector } from '@angular/core';
import type { AuthenticationData, ImagingOpenParams, PacsStudyData as PacsStudyDataNg1 } from 'estensa-module';
import { firstValueFrom } from 'rxjs';

import { RadioSettingsService } from '../settings/radio-settings.service';
import {
    IImagingServiceNg1,
    IViewerHelper,
    PacsStudyDataVnext,
    RADIOLOGY_API_CLIENT_IMAGING_SERVICE,
    toNg1PacsStudyData,
    fromNg1PacsStudyData,
    ExaminationStatusForImaging,
    ModelUSStressEchoAssociateImages,
    KeyImage,
    ModelShowImage,
    ModelUSMeasure,
} from './common';
import { EstensaReviewViewerHelper } from './estensa-review';
import { SeWebViewerHelper } from './se-web-viewer/se-web-viewer.helper';

@Injectable({
    providedIn: 'root',
})
export class ImagingService {
    private _viewerHelper: IViewerHelper | null = null;

    constructor(
        @Inject(RADIOLOGY_API_CLIENT_IMAGING_SERVICE) private imagingServiceNg1: IImagingServiceNg1,
        private injector: Injector,
        private radioSettingsService: RadioSettingsService,
    ) {}

    get viewerHelper() {
        return this._viewerHelper;
    }

    async initService() {
        if (this.viewerHelper !== null) {
            return;
        }

        const viewerType = await this.getViewerType();
        if (viewerType === 'EstensaReviewVNext') {
            // In caso di EstensaReviewVNext, apri il viewer al login, se possibile, tramite isIntegrationApp,
            //   per evitare un popup al login.
            const helper = this.injector.get(EstensaReviewViewerHelper);
            this._viewerHelper = helper;
            await helper.init();
            await helper.openViewer(true);
        } else if (viewerType === 'SeWebViewer') {
            const helper = this.injector.get(SeWebViewerHelper);
            this._viewerHelper = helper;
            await helper.init();
            await helper.openViewer();
        }
        // Aggiungere qua altre integrazioni vNext.
        // ...

        if (this.viewerHelper) {
            this.imagingServiceNg1.setViewerHelper(this.viewerHelper);
        }
    }

    async getViewerType(): Promise<string> {
        const settings = await firstValueFrom(this.radioSettingsService.radioSettingsAsync);
        const viewer = settings.viewerSettings?.webViewerType;

        if (viewer === 'EstensaReview' && settings.extensaSettings.isWebApiEnabled) {
            return 'EstensaReviewVNext';
        }
        return viewer;
    }

    areImagesOpen() {
        if (this.viewerHelper) {
            // TODO: (34.11) Non esiste ancora un'API per sapere se sono presenti immagini nel visualizzatore.
            return true;
        }

        return this.imagingServiceNg1.areImagesOpen;
    }

    async openViewer() {
        return this.viewerHelper?.openViewer();
    }

    async discardImages(askConfirmation?: boolean) {
        try {
            if (this.viewerHelper) {
                await this.viewerHelper.discardImages(askConfirmation);
            } else {
                await this.imagingServiceNg1.discardImages(askConfirmation);
            }
        } catch {
            /* Ignore these errors */
        }
    }

    setExaminationStatus(idExaminations: string[], status: ExaminationStatusForImaging): Promise<void> {
        return this.viewerHelper?.setExaminationStatus?.(idExaminations, status);
    }

    deleteKeyImages(images: KeyImage[]): Promise<unknown> {
        const promises = images.map((img) =>
            this.viewerHelper?.deleteKeyImage?.({
                instanceUID: img.SOPInstanceUID,
                isDeletable: true,
                nPla: null,
                repositoryKey: '',
                requestDeleteAll: false,
                requestRefreshIcon: true,
            }),
        );
        return Promise.all(promises);
    }

    usStressEchoSelectStage(stage: string): Promise<void> {
        return this.viewerHelper?.usStressEchoSelectStage?.(stage);
    }

    usStressEchoSelectView(view: string): Promise<void> {
        return this.viewerHelper?.usStressEchoSelectView?.(view);
    }

    uSStressEchoAssociateImages(model: ModelUSStressEchoAssociateImages): Promise<void> {
        return this.viewerHelper?.uSStressEchoAssociateImages?.(model);
    }

    showImage(model: ModelShowImage): Promise<void> {
        return this.viewerHelper?.showImage?.(model);
    }

    uSMeasureShow(model: ModelUSMeasure): Promise<void> {
        return this.viewerHelper?.uSMeasureShow?.(model);
    }

    /**
     * @deprecated Usare viewImages.
     */
    async viewImagesNg1(
        data: PacsStudyDataNg1,
        credentials: AuthenticationData,
        options: Partial<ImagingOpenParams>,
    ): Promise<void> {
        if (this.viewerHelper) {
            return this.viewImages([fromNg1PacsStudyData(data)]);
        }

        this.imagingServiceNg1.openImages(data, credentials, options);
    }

    async viewImages(data: PacsStudyDataVnext[]): Promise<void> {
        if (this.viewerHelper) {
            return this.viewerHelper.openImages(data);
        }

        // Ho dati vNext da dare a servizio ng1.
        // await necessaria per passare da void[] a void.
        await Promise.all(data.map((d) => this.imagingServiceNg1.openImages(toNg1PacsStudyData(d), null, {})));
    }
}
